import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes, { any } from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Draggable } from 'react-beautiful-dnd';
import moment from 'moment-timezone';

import {
    addOpacityToHexColor,
    getMatchDroppedDisplayString,
    getRoundName,
    hasPositionWon,
} from '../../../utils/courtSchedular';
import { TIME_FORMAT_12H } from '../../../utils/formatting';
import { getLabelByTypeId } from '../../../utils/types';
import { OutComeTypes } from '../../../utils/postScoreConstants';
import { MatchSchedularStatusTypes, MatchStatusTypes, allowPostScore, canPauseMatch, canStartMatch, formatElapsedTime, getElapsedTime, getMatchStatus, isMatchDraggable, shouldDisplayElapsedTimes, shouldDisplayMatchStatus } from '../../../utils/nextAvailable';
import { startAMatchOnCourt, pauseAMatchOnCourt } from '../../../actions/next-available-actions';
import * as drawEditActions from '../../../actions/draw-edit-actions';

import styles from './nextAvailable.scss';
import PrintMatchSheetModalV2 from '../PrintMatchSheetModalV2';
import { isPickleBallSportTypeEvent } from '../../../utils/events';
import { sportTypeIds } from '../../../utils/eventConstants';
import MatchPlayerOrTeamDetails from '../CourtSchedular/MatchPlayerOrTeamDetails';

const DraggableMatch = (props) => {
    const {
        eventSchedule,
        eventId,
        sportTypeId,
        index,
        match,
        playersDictByMemberId,
        teamsDict,
        isScheduled = false,
        className,
        onPostScoreClick,
        statusType,
        'data-testid': dataTestId,
    } = props;

    const dispatch = useDispatch();
    const history = useHistory();
    const coreUI = useSelector(({ coreUI }) => coreUI);
    const drawEdit = useSelector(({ drawEdit }) => drawEdit);
    const [showConfirmPrintPopup, setShowConfirmPrintPopup] = useState(false);

    const drawOfficials = drawEdit.officials?.data || [];
    const matchOfficial = drawOfficials.find((official) => {
        return official.id === match.officialId;
    });

    const shouldDisplayTime = useMemo(() => {
        if (statusType === MatchStatusTypes.ON_COURT) {
            return !!match.schedulerStatus;
        }

        return statusType === MatchStatusTypes.IN_QUEUE ||
            statusType === MatchStatusTypes.DONE;
    }, [match, statusType]);

    const timeToDisplay = useMemo(() => {
        if (statusType === MatchStatusTypes.ON_COURT || statusType === MatchStatusTypes.DONE) {
            return match.dateTimeStarted;
        }

        if (statusType === MatchStatusTypes.IN_QUEUE) {
            return match.dateTimeInQueue;
        }

        return null;
    }, [match, statusType]);

    const {
        division: {
            tournament: {
                scoringTypes,
            },
        },
    } = coreUI.types.tms;

    const customStyles = {
        border: `2px solid ${addOpacityToHexColor(match.drawColor, 0.4)}`,
        backgroundColor: addOpacityToHexColor(match.drawColor, 0.1),
        height: isScheduled ? '100%' : 'auto',
        marginTop: isScheduled ? '0' : '4px',
        marginBottom: isScheduled ? '0' : '4px',
    };

    const matchStatusStyles = {
        backgroundColor: (match.drawMatchStatus === MatchStatusTypes.DONE || match.schedulerStatus === MatchSchedularStatusTypes.COMPLETED)
            ? 'black'
            : match.drawColor,
    };

    const [elapsedTime, setElapsedTime] = useState({
        shouldDisplay: false,
        value: '0 min',
        hasCrossedAllottedTime: false,
    });

    const scoringType = getLabelByTypeId(scoringTypes, match.scoringTypeId);

    const hasResult = match?.result;

    const hasPosition1WonGame = hasResult && hasPositionWon('position1', match, playersDictByMemberId, teamsDict);
    const hasPosition2WonGame = hasResult && hasPositionWon('position2', match, playersDictByMemberId, teamsDict);

    const scoresArray = useMemo(() => {
        if (match?.result?.score) {
            return Object.values(match.result.score).map((score) => {
                const winnerScore = score.winner ? score.winner : 0;
                const loserScore = score.loser ? score.loser : 0;
                return {
                    ...score,
                    winner: winnerScore,
                    loser: loserScore,
                };
            });
        }

        return [];
    }, []);

    const onClickMatch = (ev) => {
        ev.preventDefault();
        ev.stopPropagation();

        if (allowPostScore(match)) {
            onPostScoreClick(match);
        }
    };

    const onClickStartMatch = (ev) => {
        ev.preventDefault();
        ev.stopPropagation();

        dispatch(startAMatchOnCourt(eventId, match));
    };

    const onClickPauseMatch = (ev) => {
        ev.preventDefault();
        ev.stopPropagation();

        dispatch(pauseAMatchOnCourt(eventId, match));
    };

    const onPrintClick = (ev) => {
        ev.preventDefault();
        ev.stopPropagation();

        setShowConfirmPrintPopup(true);
    };

    const onConfirmPrint = (formValues) => {
        const { scoreSheetType, pageFormat } = formValues;
        if (formValues.markPrinted) {
            dispatch(drawEditActions.printMatchSheet({ reload: true, matchId: match.id }));
        }
        if (window) {
            window.open(`/events/${eventId}/draws/${match.drawId}/print-matches?matchId=${match.id}&scoringFormat=${match.scoringTypeId}&scoreSheetType=${scoreSheetType}&pageFormat=${pageFormat}`, '_blank');
        } else {
            history.push(`/events/${eventId}/draws/${match.drawId}/print-matches?matchId=${match.id}&scoringFormat=${match.scoringTypeId}&scoreSheetType=${scoreSheetType}&pageFormat=${pageFormat}`);
        }
        setShowConfirmPrintPopup(false);
    };

    const isGameDroppedByTeam = match.result?.outcome === OutComeTypes.DEFAULTED ||
        match.result?.outcome === OutComeTypes.WITHDREW ||
        match.result?.outcome === OutComeTypes.RETIRED ||
        match.result?.outcome === OutComeTypes.FORFEIT;

    const shouldDisplayStatus = shouldDisplayMatchStatus(match);

    const updateElapsedTimes = useCallback(() => {
        const time = getElapsedTime(match);
        setElapsedTime({
            shouldDisplay: shouldDisplayElapsedTimes(match),
            value: time,
            hasCrossedAllottedTime: time > match.allottedMatchTime,
        });
    }, [match]);

    useEffect(() => {
        let interval;
        if (isScheduled && (match.drawMatchStatus === MatchStatusTypes.ON_COURT || match.drawMatchStatus === MatchStatusTypes.DONE)) {
            updateElapsedTimes();

            if (match.schedulerStatus === MatchSchedularStatusTypes.IN_PROGRESS) {
                interval = setInterval(() => {
                    updateElapsedTimes();
                }, 60000);
            }
        }

        return () => {
            if (interval) {
                clearInterval(interval);
            }
        };
      }, [match.drawMatchStatus, match.schedulerStatus, isScheduled, updateElapsedTimes]);

    return (
        <>
            <Draggable
                draggableId={match.id}
                index={index}
                isDragDisabled={!isMatchDraggable(match)}
            >
                {(provided) => (
                    <div
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        ref={provided.innerRef}
                    >
                        <div
                            className={`${styles.draggableMatch} ${className}`}
                            style={customStyles}
                            onClick={onClickMatch}
                            data-testid={dataTestId}
                        >
                            {shouldDisplayStatus && (
                                <div className={styles.statusLayout}>
                                    <div className={styles.matchStatus} style={matchStatusStyles} data-testid={dataTestId && `${dataTestId}.status`}>
                                        {getMatchStatus(match)}
                                    </div>
                                </div>
                            )}
                            <div className={styles.actions}>
                                {isPickleBallSportTypeEvent(sportTypeId) && (
                                    <button
                                        type="button"
                                        className={styles.actionButton}
                                        onClick={onPrintClick}
                                        data-testid={dataTestId && `${dataTestId}.print-btn`}
                                    >
                                        <i className={`fas fa-solid fa-print`} />
                                    </button>
                                )}
                                {isScheduled && canStartMatch(match) && (
                                    <button
                                        type="button"
                                        className={styles.actionButton}
                                        onClick={onClickStartMatch}
                                        data-testid={dataTestId && `${dataTestId}.play-btn`}
                                    >
                                        <i className={`fas fa-solid fa-play`} />
                                    </button>
                                )}
                                {isScheduled && canPauseMatch(match) && (
                                    <button
                                        type="button"
                                        className={styles.actionButton}
                                        onClick={onClickPauseMatch}
                                        data-testid={dataTestId && `${dataTestId}.pause-btn`}
                                    >
                                        <i className={`fas fa-solid fa-pause`} />
                                    </button>
                                )}
                            </div>
                            <div className={`${styles.matchData} ${shouldDisplayStatus ? styles.matchStatus : ''}`} data-testid={dataTestId && `${dataTestId}.match-data`}>
                                {shouldDisplayTime && timeToDisplay && (
                                    <div className={`${styles.startTime}`} data-testid={dataTestId && `${dataTestId}.start-time`}>
                                        {moment.utc(timeToDisplay).tz(eventSchedule?.timeZone?.value).format(TIME_FORMAT_12H)}
                                        {elapsedTime.shouldDisplay && (
                                            <span className={`${styles.elapsedTime} ${elapsedTime.hasCrossedAllottedTime ? styles.overrun : ''}`} data-testid={dataTestId && `${dataTestId}.elapsed-time`}>
                                                {formatElapsedTime(elapsedTime.value)}
                                            </span>
                                        )}
                                    </div>
                                )}
                                <MatchPlayerOrTeamDetails
                                    className={`${styles.player}  mb2 ${hasPosition1WonGame ? styles.wonMatch : ''}`}
                                    positionParam="position1"
                                    match={match}
                                    playersDictByMemberId={playersDictByMemberId}
                                    teamsDict={teamsDict}
                                    data-testid={dataTestId && `${dataTestId}.position1-player`}
                                />
                                <MatchPlayerOrTeamDetails
                                    className={`${styles.player} mb4 ${hasPosition2WonGame ? styles.wonMatch : ''}`}
                                    positionParam="position2"
                                    match={match}
                                    playersDictByMemberId={playersDictByMemberId}
                                    teamsDict={teamsDict}
                                    data-testid={dataTestId && `${dataTestId}.position2-player`}
                                />
                                <div className={`${styles.details}`} data-testid={dataTestId && `${dataTestId}.parent-draw-name`}>
                                    {match.parentDrawName}
                                </div>
                                <div className={`${styles.details}`} data-testid={dataTestId && `${dataTestId}.draw-name`}>
                                    {`${match.drawName}, ${getRoundName(match)}`}
                                </div>
                                {scoringType && (
                                    <div className={`${styles.details}`} data-testid={dataTestId && `${dataTestId}.scoring-type`}>
                                        {scoringType}
                                    </div>
                                )}
                                {(hasResult || matchOfficial) && <div className="mb4" />}
                                {hasResult && (scoresArray.length > 0 || isGameDroppedByTeam) && (
                                    <div className={`${styles.details}`} data-testid={dataTestId && `${dataTestId}.score`}>
                                        {scoresArray.map((score, index, array) => {
                                            return (
                                                <div className={styles.setScore} key={`match-${match.id}-score-${index}`}>
                                                    <span className={styles.winner}>{score.winner}</span>-{score.loser}{score.tiebreak && `(${score.tiebreak})`}{index !== (array.length - 1) && ','}
                                                </div>
                                            );
                                        })}
                                        {isGameDroppedByTeam && (getMatchDroppedDisplayString(match, playersDictByMemberId, teamsDict))}
                                    </div>
                                )}
                                {matchOfficial && (
                                    <div className={`${styles.details}`} data-testid={dataTestId && `${dataTestId}.official`}>
                                        {`R: ${matchOfficial?.firstName} ${matchOfficial?.lastName}`}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </Draggable>
            {showConfirmPrintPopup && (
                <PrintMatchSheetModalV2
                    onCloseClick={() => setShowConfirmPrintPopup(false)}
                    onConfirmPrint={onConfirmPrint}
                    showMarkPrinted={true}
                />
            )}
        </>
    );
};

DraggableMatch.propTypes = {
    match: PropTypes.objectOf(any).isRequired,
    playersDictByMemberId: PropTypes.objectOf(any),
    teamsDict: PropTypes.objectOf(any),
    isScheduled: PropTypes.bool,
    index: PropTypes.number,
    className: PropTypes.string,
    eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    onPostScoreClick: PropTypes.func.isRequired,
    eventSchedule: PropTypes.objectOf(any).isRequired,
    sportTypeId: PropTypes.number,
    statusType: PropTypes.number,
    'data-testid': PropTypes.string,
};

DraggableMatch.defaultProps = {
    isScheduled: false,
    playersDictByMemberId: {},
    teamsDict: {},
    index: 0,
    className: '',
    statusType: MatchStatusTypes.NONE,
    sportTypeId: sportTypeIds.TENNIS,
    'data-testid': undefined,
};

export default DraggableMatch;
